/**
 * VoiceTest — Entrepreneur-only test panel for ElevenLabs streaming TTS.
 * Lives in SettingsPage. Validates browser playback before building real voice mode.
 */

import { useState, useRef } from 'react';
import { Volume2, Loader2, Square, Mic2, Mic, MicOff } from 'lucide-react';
import { speakStream, transcribeAudio } from '../lib/api';
import { playStreamingMp3, isMseSupported } from '../lib/voicePlayer';
import { useMediaRecorder } from '../lib/useMediaRecorder';

const SAMPLE_TEXTS = [
  "That sounds like it weighed on you. Before we move on — what would 'enough' look like tomorrow?",
  "You named three things you wanted to do today. Two are done. The third — what got in the way?",
  "I notice you keep coming back to this. That's worth paying attention to, not pushing past.",
];

export default function VoiceTest() {
  const [text, setText] = useState(SAMPLE_TEXTS[0]);
  const [loading, setLoading] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [timings, setTimings] = useState({});
  const [error, setError] = useState(null);
  const [transcribing, setTranscribing] = useState(false);
  const [sttTimings, setSttTimings] = useState({});
  const playerRef = useRef(null);

  const { isRecording, isSupported: micSupported, start: startRec, stop: stopRec, durationMs } = useMediaRecorder();

  const handleRecord = async () => {
    setError(null);
    try {
      await startRec();
    } catch (e) {
      setError(e.message);
    }
  };

  const handleStopAndTranscribe = async () => {
    const blob = await stopRec();
    if (!blob) return;
    setTranscribing(true);
    setSttTimings({ blobBytes: blob.size, blobMime: blob.type });
    const t0 = performance.now();
    try {
      const result = await transcribeAudio(blob);
      setSttTimings(t => ({
        ...t,
        responseMs: Math.round(performance.now() - t0),
        chars: result.text.length,
      }));
      if (result.text) {
        setText(result.text);
      }
    } catch (e) {
      setError(`Transcribe: ${e.message}`);
    } finally {
      setTranscribing(false);
    }
  };

  const handleSpeak = async () => {
    if (loading || playing) return;
    setLoading(true);
    setError(null);
    setTimings({ requested: 0 });
    const reqStart = performance.now();

    try {
      const response = await speakStream(text);
      setTimings(t => ({ ...t, response: Math.round(performance.now() - reqStart) }));

      const player = await playStreamingMp3(response, {
        onTiming: (stage, info) => {
          setTimings(t => ({ ...t, [stage]: Math.round(info.ms) }));
        },
        onEnded: () => {
          setPlaying(false);
          playerRef.current = null;
        },
        onError: (e) => {
          setError(e.message);
          setPlaying(false);
        },
      });
      playerRef.current = player;
      setPlaying(true);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleStop = () => {
    if (playerRef.current) {
      playerRef.current.stop();
      playerRef.current = null;
      setPlaying(false);
    }
  };

  const mseOK = isMseSupported();

  return (
    <div className="bg-slate-800/60 border border-slate-700/50 rounded-lg p-5">
      <div className="flex items-center gap-2 mb-4">
        <Mic2 size={16} className="text-purple-400" />
        <h3 className="text-sm font-medium text-slate-200">Voice Test (Beta)</h3>
        <span className="text-[11px] text-slate-500 ml-auto">
          {mseOK ? 'MSE: streaming' : 'Fallback: full-download'}
        </span>
      </div>

      <div className="space-y-3">
        <div className="flex flex-wrap items-center gap-1.5">
          {SAMPLE_TEXTS.map((sample, i) => (
            <button
              key={i}
              onClick={() => setText(sample)}
              disabled={loading || playing || isRecording || transcribing}
              className="text-[11px] px-2 py-1 rounded-full bg-slate-700/50 text-slate-400 hover:text-slate-200 hover:bg-slate-700 disabled:opacity-40 transition-colors"
            >
              Sample {i + 1}
            </button>
          ))}
          <span className="text-[11px] text-slate-600 ml-auto">
            mic: {micSupported ? 'ready' : 'unsupported'}
          </span>
        </div>

        {/* Record button — STT */}
        <div className="flex items-center gap-2">
          {!isRecording ? (
            <button
              onClick={handleRecord}
              disabled={!micSupported || loading || playing || transcribing}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-500 disabled:bg-slate-700 disabled:text-slate-500 text-white text-xs font-medium rounded-md transition-colors min-h-tap sm:min-h-0"
              title="Record audio to transcribe"
            >
              {transcribing ? <Loader2 size={14} className="animate-spin" /> : <Mic size={14} />}
              {transcribing ? 'Transcribing...' : 'Record'}
            </button>
          ) : (
            <button
              onClick={handleStopAndTranscribe}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-500 text-white text-xs font-medium rounded-md transition-colors min-h-tap sm:min-h-0 animate-pulse"
            >
              <MicOff size={14} /> Stop & Transcribe
            </button>
          )}
          {isRecording && (
            <span className="text-xs text-red-400 font-mono">
              ● {(durationMs / 1000).toFixed(1)}s
            </span>
          )}
        </div>

        {sttTimings.responseMs !== undefined && (
          <div className="text-[11px] text-slate-500 font-mono bg-slate-900/40 border border-slate-700/40 rounded-md px-3 py-2 grid grid-cols-2 gap-x-4 gap-y-0.5">
            <span>blob</span><span className="text-slate-300">{(sttTimings.blobBytes / 1024).toFixed(1)} KB · {sttTimings.blobMime}</span>
            <span>STT roundtrip</span><span className="text-slate-300">{sttTimings.responseMs}ms</span>
            <span>transcript</span><span className="text-emerald-400">{sttTimings.chars} chars</span>
          </div>
        )}

        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={3}
          maxLength={2000}
          disabled={loading || playing}
          className="w-full bg-slate-900 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-blue-500 resize-none"
          placeholder="Text the Navigator will speak..."
        />

        <div className="flex items-center gap-2">
          {!playing ? (
            <button
              onClick={handleSpeak}
              disabled={loading || !text.trim()}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 disabled:bg-slate-700 disabled:text-slate-500 text-white text-xs font-medium rounded-md transition-colors min-h-tap sm:min-h-0"
            >
              {loading ? <Loader2 size={14} className="animate-spin" /> : <Volume2 size={14} />}
              {loading ? 'Connecting...' : 'Speak'}
            </button>
          ) : (
            <button
              onClick={handleStop}
              className="flex items-center gap-2 px-4 py-2 bg-red-900/50 hover:bg-red-900/70 border border-red-800/50 text-red-200 text-xs font-medium rounded-md transition-colors min-h-tap sm:min-h-0"
            >
              <Square size={12} /> Stop
            </button>
          )}
          <span className="text-[11px] text-slate-500">{text.length} chars</span>
        </div>

        {error && (
          <p className="text-xs text-red-400 bg-red-900/20 border border-red-900/40 rounded-md px-3 py-2">
            {error}
          </p>
        )}

        {Object.keys(timings).length > 0 && (
          <div className="text-[11px] text-slate-500 font-mono bg-slate-900/40 border border-slate-700/40 rounded-md px-3 py-2 grid grid-cols-2 gap-x-4 gap-y-0.5">
            {timings.response !== undefined && <><span>request → response</span><span className="text-slate-300">{timings.response}ms</span></>}
            {timings['first-byte'] !== undefined && <><span>response → first byte</span><span className="text-slate-300">{timings['first-byte']}ms</span></>}
            {timings['first-buffer'] !== undefined && <><span>first buffer ready</span><span className="text-slate-300">{timings['first-buffer']}ms</span></>}
            {timings.playing !== undefined && <><span className="text-emerald-400">audio playing</span><span className="text-emerald-400">{timings.playing}ms</span></>}
            {timings['upstream-done'] !== undefined && <><span>full body received</span><span className="text-slate-300">{timings['upstream-done']}ms</span></>}
            {timings.ended !== undefined && <><span>playback ended</span><span className="text-slate-300">{timings.ended}ms</span></>}
          </div>
        )}
      </div>
    </div>
  );
}
